import { useResourceParams, useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useResourceInvalidate } from "./useResourceInvalidate";
import { useResourceContext } from "@/layouts/ResourceContext";

export const useResourceUpdate = (props = {}) => {
  const { invalidate: invalidateFromProps, resource: resourceFromProps } = props;
  const { t } = useTranslation();
  const { resource: resourceDef, id, action } = useResourceParams();
  const invalidate = useResourceInvalidate();

  const { tenantName } = useResourceContext();
  const meta = {}
  if(tenantName) {
    meta.tenantName = tenantName;
  }

  let resource = resourceFromProps ?? resourceDef.name;

  const { mutate: originalMutate, mutateAsync: originalMutateAsync, isLoading, overtime } = useUpdate({
    successNotification: (response, { id, values }, resource) => {
      const { action, data } = values;
      invalidate({
        resource,
        invalidates: ["list"],
        ...invalidateFromProps,
      });
      return {
        message: t(`${resource}.actionResult.${action}.success`, { id, ...values?.data }),
        description: "Success",
        type: "success",
      };
    },
    errorNotification: (error, { id, values }, resource) => {
      const { action } = values;
      return {
        message: t(`${resource}.actionResult.${action}.failed`, { id }),
        type: "error"
      };
    },
    meta
  });

  /**
   * Mutates a resource with the given parameters.
   * 
   * @param variables - The variables for the mutation.
   *   - action: The action to perform on the resource. Possible values are 'start', 'stop', 'restart', 'fetch', and 'update'.
   *   - id: The ID of the instance.
   *   - values: Optional. The updated values for the instance.
   * @returns A promise that resolves to the result of the mutation.
   */
  const mutate = async (variables) => {
    const { action: actionFromVariables, id: idFromVariables, values: data, ...rest } = variables;
    return await originalMutate({
      resource,
      id: idFromVariables ?? id,
      values: {
        action: actionFromVariables ?? action,
        data,
      },
      ...rest,
    });
  };
  const mutateAsync = async (variables) => {
    const { action: actionFromVariables, id: idFromVariables, values: valueData, data, ...rest } = variables;
    return await originalMutateAsync({
      resource,
      id: idFromVariables ?? id,
      values: {
        action: actionFromVariables ?? action,
        data: valueData ?? data,
      },
      ...rest,
    });
  };
  return {
    mutate,
    mutateAsync,
    isLoading,
    overtime,
  };
};